import { RouteContext } from '@ant-design/pro-components';
import { useQueryClient } from '@tanstack/react-query';
import { ConfigProvider, type ConfigProviderProps, type ThemeConfig } from 'antd';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import MultiTabs from './MultiTabs';
import { useAppLayoutStyles } from './styles';

const pageCardConfig: ConfigProviderProps['card'] = { variant: 'borderless' };
const pageTheme: ThemeConfig = {
  components: { Card: { boxShadowTertiary: 'none' } },
};

export default function TabWorkspace({
  isMobile,
  siderWidth,
  footer,
}: {
  isMobile: boolean;
  siderWidth: number;
  footer?: ReactNode;
}) {
  const { styles } = useAppLayoutStyles();
  const routeContext = useContext(RouteContext);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const getTargetContainer = useCallback(
    () => workspaceRef.current ?? document.body,
    [],
  );
  const queryClient = useQueryClient();
  const location = useLocation();
  const [revision, setRevision] = useState(0);
  const [refreshingLocation, setRefreshingLocation] = useState<string | null>(null);
  const isReloading = refreshingLocation === location.key;

  useEffect(() => {
    if (!revision) return;
    let current = true;
    const requests = queryClient
      .getQueryCache()
      .findAll({ type: 'active', fetchStatus: 'fetching' })
      .flatMap((query) => (query.promise ? [query.promise] : []));
    void Promise.allSettled(requests).then(() => {
      if (current) setRefreshingLocation(null);
    });
    return () => {
      current = false;
    };
  }, [revision, queryClient]);

  function reloadPage() {
    if (isReloading) return;
    // 仅重新挂载页面；不导航、不改变query/hash，由新页面的查询观察器触发请求。
    void queryClient.invalidateQueries({ refetchType: 'none' });
    setRefreshingLocation(location.key);
    setRevision((value) => value + 1);
  }
  return (
    <div ref={workspaceRef} className={styles.workspace} data-testid="tab-workspace">
      <MultiTabs
        isMobile={isMobile}
        isReloading={isReloading}
        onReload={reloadPage}
        workspaceRef={workspaceRef}
      />
      {/* 页面和页脚共用滚动层；头部、标签与其弹层不随内容滚动。 */}
      <div className={styles.pageScroll} data-page-scroll data-testid="page-scroll">
        <div
          className={styles.pageArea}
          data-testid="page-content"
          aria-busy={isReloading}
          key={revision}
        >
          <ConfigProvider
            getTargetContainer={getTargetContainer}
            card={pageCardConfig}
            theme={pageTheme}
          >
            {/* ProLayout 折叠态上下文固定为 64px；底栏需要壳的实际侧栏宽度。 */}
            <RouteContext.Provider value={{ ...routeContext, siderWidth }}>
              <Outlet />
            </RouteContext.Provider>
          </ConfigProvider>
        </div>
        {footer}
      </div>
    </div>
  );
}
